import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";

import ContractorProfileContent from "../ContractorProfileContent";
import { FirebaseAuth } from "../FirebaseAuth/firebase";
import { formatLabel, getServiceLabel, getServiceLevelLabel } from "../../libs/labels";

const EXAMPLE_QUERIES = [
  "Data engineers with BigQuery and Airflow under $35/hr",
  "Analytics engineers with Looker and SQL",
  "Platform engineers with Terraform and GCP",
];

const formatCurrency = (amountInCents, currency = "usd") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: String(currency || "usd").toUpperCase(),
  }).format(Number(amountInCents || 0) / 100);

const readField = (row = {}, snakeKey, fallback = undefined) => {
  if (row && Object.prototype.hasOwnProperty.call(row, snakeKey)) return row[snakeKey];
  return fallback;
};

const normalizeResult = (row = {}) => ({
  uid: String(row.uid || ""),
  displayName: String(readField(row, "display_name", "Contractor") || "Contractor"),
  service: String(row.service || ""),
  level: String(row.level || ""),
  currency: String(row.currency || "usd"),
  hourlyCents: Number(readField(row, "hourly_cents", 0) || 0),
  matchScore: Number(readField(row, "match_score", 0) || 0),
  matchReason: String(readField(row, "match_reason", "") || ""),
  matchedTerms: Array.isArray(readField(row, "matched_terms", []))
    ? readField(row, "matched_terms", [])
    : [],
  profile: row.profile || {},
});

const ContractorProfileDialog = ({ contractor, onClose }) => {
  const profile = contractor?.profile || {};

  return (
    <Dialog open={!!contractor} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pr: 7, py: 2.25 }}>
        Contractor Profile
        <IconButton
          aria-label="Close contractor profile"
          onClick={onClose}
          sx={{ position: "absolute", right: 14, top: 14 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, md: 3 } }}>
        {contractor && (
          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "flex-start", sm: "center" }}>
              <Avatar variant="rounded" sx={{ width: 72, height: 72, bgcolor: "#e8f0fe", color: "primary.main" }}>
                <DescriptionOutlinedIcon fontSize="large" />
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h5" sx={{ fontWeight: 850 }}>
                  {contractor.displayName}
                </Typography>
                <Typography color="text.secondary">
                  {getServiceLabel(contractor.service)} · {getServiceLevelLabel(contractor.service, contractor.level)}
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip size="small" color="primary" label={`${Math.round(contractor.matchScore)}% match`} sx={{ borderRadius: 1 }} />
                  {contractor.hourlyCents > 0 && (
                    <Chip size="small" label={`${formatCurrency(contractor.hourlyCents, contractor.currency)}/hr`} sx={{ borderRadius: 1 }} />
                  )}
                </Stack>
              </Box>
            </Stack>

            {contractor.matchReason && (
              <Alert icon={<AutoAwesomeOutlinedIcon />} severity="info" sx={{ borderRadius: 2 }}>
                {contractor.matchReason}
              </Alert>
            )}

            <ContractorProfileContent profile={profile} emptyMessage="No profile summary has been shared yet." />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const AiContractorSearch = ({
  title = "AI contractor search",
  description = "Search contractors using natural language.",
  autoSearch = false,
  compact = false,
  initialQuery = "",
  showExamples = true,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedContractor, setSelectedContractor] = useState(null);

  const visibleResults = useMemo(() => results.map(normalizeResult), [results]);

  const runSearch = useCallback(async (nextQuery = query) => {
    const value = String(nextQuery || "").trim();
    if (value.length < 3 || loading) return;
    setQuery(value);
    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const fn = FirebaseAuth.functions().httpsCallable("searchApprovedContractors");
      const response = await fn({ query: value });
      setResults(Array.isArray(response?.data?.results) ? response.data.results : []);
    } catch (err) {
      setResults([]);
      setError(
        String(err?.message || "")
          .replace(/^Firebase:\s*/i, "")
          .replace(/\s*\(functions\/[^)]+\)\.?$/i, "")
          .trim() || "Search failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [loading, query]);

  useEffect(() => {
    const value = String(initialQuery || "").trim();
    setQuery(value);
    if (autoSearch && value.length >= 3) {
      runSearch(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, initialQuery]);

  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, md: compact ? 2 : 2.5 }, borderRadius: 2, borderColor: "#dadce0" }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between">
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar variant="rounded" sx={{ bgcolor: "#e8f0fe", color: "primary.main", width: 36, height: 36 }}>
              <AutoAwesomeOutlinedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 850 }}>{title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") runSearch();
            }}
            placeholder='Try "data engineers with BigQuery and Airflow under $35/hr"'
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            disabled={loading || query.trim().length < 3}
            onClick={() => runSearch()}
            sx={{ textTransform: "none", fontWeight: 800, minWidth: 120 }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
        </Stack>

        {showExamples && !searched && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {EXAMPLE_QUERIES.map((example) => (
              <Chip
                key={example}
                icon={<TuneOutlinedIcon />}
                label={example}
                clickable
                onClick={() => runSearch(example)}
                sx={{ borderRadius: 1.5, maxWidth: "100%" }}
              />
            ))}
          </Stack>
        )}

        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        {loading ? (
          <Stack spacing={1.25}>
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} variant="rounded" height={118} />
            ))}
          </Stack>
        ) : searched && visibleResults.length === 0 && !error ? (
          <Box sx={{ py: 3, textAlign: "center", border: "1px dashed #dadce0", borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 800 }}>No contractors matched that search.</Typography>
            <Typography variant="body2" color="text.secondary">
              Try a broader role, fewer tools, or a higher hourly rate.
            </Typography>
          </Box>
        ) : visibleResults.length > 0 ? (
          <Stack spacing={1.25}>
            {visibleResults.map((contractor) => {
              const terms = contractor.matchedTerms.slice(0, 5);
              return (
                <Paper
                  key={contractor.uid}
                  variant="outlined"
                  onClick={() => setSelectedContractor(contractor)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    transition: "background-color 140ms ease, border-color 140ms ease",
                    "&:hover": { bgcolor: "#f8fafc", borderColor: "primary.light" },
                  }}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "flex-start", sm: "center" }}>
                    <Avatar variant="rounded" sx={{ bgcolor: "#e8f0fe", color: "primary.main", width: 52, height: 52 }}>
                      <DescriptionOutlinedIcon />
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography sx={{ fontWeight: 850 }}>{contractor.displayName}</Typography>
                        <Chip size="small" color="primary" label={`${Math.round(contractor.matchScore)}%`} sx={{ borderRadius: 1 }} />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {getServiceLabel(contractor.service)} · {getServiceLevelLabel(contractor.service, contractor.level)}
                        {contractor.hourlyCents > 0 ? ` · ${formatCurrency(contractor.hourlyCents, contractor.currency)}/hr` : ""}
                      </Typography>
                      {contractor.matchReason && (
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                          {contractor.matchReason}
                        </Typography>
                      )}
                      {!!terms.length && (
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                          {terms.map((term) => (
                            <Chip key={term} size="small" label={formatLabel(term)} sx={{ borderRadius: 1 }} />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        ) : null}
      </Stack>

      <ContractorProfileDialog contractor={selectedContractor} onClose={() => setSelectedContractor(null)} />
    </Paper>
  );
};

export default AiContractorSearch;
